import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type {
  FilterSectionKey,
  TerminalFiltersApplied,
  TerminalFiltersDraft,
  UseTerminalFiltersParams,
} from './use-terminal-filters.type';

const resolveIdOrNameFilter = (value: string) => {
  const isNumeric = /^\d+$/.test(value);
  return {
    idFilter: isNumeric ? [Number(value)] : undefined,
    nameFilter: isNumeric ? undefined : value || undefined,
  };
};

export const useTerminalFilters = ({
  onToast,
}: UseTerminalFiltersParams = {}) => {
  const { t } = useTranslation();

  const [isFilterOpen, setIsFilterOpen] = useState(false);

  const [openFilterSections, setOpenFilterSections] = useState<{
    payfac: boolean;
    seller: boolean;
    serialNumber: boolean;
  }>({
    payfac: false,
    seller: false,
    serialNumber: true,
  });

  const [draftFilters, setDraftFilters] = useState<TerminalFiltersDraft>({
    payfac: '',
    seller: '',
    serialNumberInput: '',
    serialNumbers: [],
  });

  const [appliedFilters, setAppliedFilters] = useState<TerminalFiltersApplied>({});

  const hasActiveFilters = useMemo(
    () => Object.keys(appliedFilters).length > 0,
    [appliedFilters],
  );

  const { idFilter: payfacIdFilter, nameFilter: payfacNameFilter } =
    resolveIdOrNameFilter(appliedFilters.payfac ?? '');

  const { idFilter: sellerIdFilter, nameFilter: sellerNameFilter } =
    resolveIdOrNameFilter(appliedFilters.seller ?? '');

  const serialNumbersFilter =
    appliedFilters.serialNumbers && appliedFilters.serialNumbers.length > 0
      ? appliedFilters.serialNumbers
      : undefined;

  const toggleFilterOpen = () => setIsFilterOpen((prev) => !prev);
  const closeFilter = () => setIsFilterOpen(false);

  const toggleFilterSection = (key: FilterSectionKey) => {
    setOpenFilterSections((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const updateDraftFilters = (
    updater: (prev: TerminalFiltersDraft) => TerminalFiltersDraft,
  ) => {
    setDraftFilters(updater);
  };

  const handleAddSerialFilter = () => {
    const value = draftFilters.serialNumberInput.trim();

    if (!value) return;

    if (draftFilters.serialNumbers.includes(value)) {
      onToast?.({
        variant: 'warning',
        title: t('backoffice.commonAttention'),
        description: t('backoffice.filters.serialAlreadyAdded'),
      });
      return;
    }

    setDraftFilters((prev) => ({
      ...prev,
      serialNumbers: [...prev.serialNumbers, value],
      serialNumberInput: '',
    }));
  };

  const handleRemoveSerialFilter = (serial: string) => {
    setDraftFilters((prev) => ({
      ...prev,
      serialNumbers: prev.serialNumbers.filter((item) => item !== serial),
    }));
  };

  const handleApplyFilters = () => {
    const payfac = draftFilters.payfac.trim();
    const seller = draftFilters.seller.trim();
    const { serialNumbers } = draftFilters;

    const nextApplied: TerminalFiltersApplied = {
      ...(payfac && { payfac }),
      ...(seller && { seller }),
      ...(serialNumbers.length > 0 && { serialNumbers }),
    };

    setAppliedFilters(nextApplied);
    setIsFilterOpen(false);
  };

  const handleClearAllFilters = () => {
    setDraftFilters({
      payfac: '',
      seller: '',
      serialNumberInput: '',
      serialNumbers: [],
    });
    setAppliedFilters({});
  };

  const removeAppliedFilter = (key: keyof TerminalFiltersApplied) => {
    setAppliedFilters((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });

    if (key === 'serialNumbers') {
      setDraftFilters((prev) => ({
        ...prev,
        serialNumberInput: '',
        serialNumbers: [],
      }));
    } else {
      setDraftFilters((prev) => ({
        ...prev,
        [key]: '',
      }));
    }
  };

  return {
    isFilterOpen,
    openFilterSections,
    draftFilters,
    appliedFilters,
    hasActiveFilters,
    payfacIdFilter,
    payfacNameFilter,
    serialNumbersFilter,
    sellerIdFilter,
    sellerNameFilter,
    toggleFilterOpen,
    closeFilter,
    toggleFilterSection,
    updateDraftFilters,
    handleAddSerialFilter,
    handleRemoveSerialFilter,
    handleApplyFilters,
    handleClearAllFilters,
    removeAppliedFilter,
  };
};
